import auth from "@services/auth/auth";
import { appStore } from "@store/appStore";
import { navigationStore } from "@store/navigationStore";
import { useEffect, useState } from "react";

/**
 * AuthGate ensures that it's children component will ONLY run once the 
 * auth has been settled.
 * 
 * This guarantees that all children will run with or without Auth 
 * (There were situations where we would run auth async with other calls 
 * and some calls would work and some would fail depending on when 
 * the auth was recieved)
 * 
 * It will capture the intended URL destination the user wanted to access
 * before they were redirected to the credentials page
 */
export default function AuthGate({ children }: { children: React.ReactNode }) {

    const [initialized, setInitialized] = useState<boolean>(false);

    async function checkAuth() {
        try {
            // tokenLogin uses appStore.authToken — if mid-impersonation that
            // token is bound to the target user, so loggedInUser correctly
            // hydrates as the target. Banner state lives in localStorage and
            // is restored by the store proxy initializer above.
            await auth.tokenLogin()
            // Authed — clear any stale redirect left over from a prior session.
            navigationStore.postLoginRedirect = ""
        } catch {
            console.log("Invalid Credentials");
            // token-login failed (e.g. 403 on an expired/invalid token). That
            // status doesn't trip http.ts's 401 handler, so clear the stale
            // token here — otherwise it lingers and the login route's
            // "already authed" guard would bounce the user to a landing page
            // instead of showing the login form.
            appStore.authToken = ""
            // Only stash a real destination; login.tsx validates it against the
            // user's permissions before applying. Never blank postLoginRedirect
            // here: a mid-session 401 (in http.ts) may have already saved the page
            // the user was on, and by the time this runs the URL is often "/". The
            // success path above and login.tsx clear it once it's consumed.
            const url = new URL(window.location.href);
            const redirect = `${url.pathname}${url.search}${url.hash}`;
            if (redirect !== "/" && redirect !== "/request-change-password") {
                navigationStore.postLoginRedirect = redirect
            }
        }
        setInitialized(true)
    }

    useEffect(() => {
        const url = new URL(window.location.href);

        const paramAuthToken = url.searchParams.get("authToken");

        if (paramAuthToken) {
            appStore.authToken = paramAuthToken
            const url = new URL(window.location.href);
            url.searchParams.delete('authToken');
            window.history.replaceState(null, '', url.toString());
        }

        checkAuth()
    }, [])

    if (!initialized) return null
    return <>{children}</>
}